import { MaybeDocument, ServerScope } from 'nano';
import { DbConnectionSettings } from './db-connection-settings';

interface IReplicationCreateRequest {
  name: string;
  sourceDatabase: DbConnectionSettings;
  destinationDatabase: DbConnectionSettings;
  continious: boolean;
  selector: any;
}

interface IReplicationDocument extends MaybeDocument {
  source: DbConnectionSettings;
  target: DbConnectionSettings;
  create_target: boolean;
  continuous: boolean;
  selector: any;
}
const REPLICATOR_DB = '_replicator';

export class ReplicationService {
  constructor(
    private dbInstance: ServerScope,
    private logger: any,
  ) {}

  public async addMultipleReplications(replicationCreateRequests: IReplicationCreateRequest[]) {
    await this.createReplicatorIfNotExists();
    for (const replicationCreateRequest of replicationCreateRequests) {
      try {
        await this.addReplication(replicationCreateRequest);
      } catch (e) {
        this.logger.error(`failed to create replication`, e);
      }
    }
  }

  private async createReplicatorIfNotExists() {
    const replicatorDb = await this.getReplicatorDb();
    if (!replicatorDb) {
      await this.dbInstance.db.create(REPLICATOR_DB);
    }
  }

  private async getReplicatorDb() {
    try {
      const db = await this.dbInstance.db.get(REPLICATOR_DB);
      return db;
    } catch (e: any) {
      if (e.message.indexOf(404) !== -1) {
        return null;
      }
      throw e;
    }
  }

  private async addReplication(replicationCreateRequest: IReplicationCreateRequest) {
    const existingReplication = await this.getReplicationOrNull(replicationCreateRequest.name);

    if (existingReplication) {
      this.logger.info(`replication ${replicationCreateRequest.name} already exists, removing to create & update`);
      await this.removeReplication(existingReplication);
    }

    await this.createReplication(replicationCreateRequest);
    this.logger.info(`created ${
      replicationCreateRequest.continious ? 'continious' : 'onceoff'} replication: ${replicationCreateRequest.name}`);
  }

  private async createReplication(replicationCreateRequest: IReplicationCreateRequest) {
    const replicatorDb = this.dbInstance.use(REPLICATOR_DB);
    const replicationDocument: IReplicationDocument = {
      _id: replicationCreateRequest.name,
      source: replicationCreateRequest.sourceDatabase,
      target: replicationCreateRequest.destinationDatabase,
      create_target: true,
      continuous: replicationCreateRequest.continious,
      selector: replicationCreateRequest.selector,
    };
    const createdReplication = await replicatorDb.insert(replicationDocument);
    return createdReplication;
  }

  private async getReplicationOrNull(name: string) {
    try {
      const existingReplication = await this.dbInstance.db.replication.query(
        name,
        {},
      );
      return existingReplication;
    } catch (e) {
      if (e.message.indexOf('404') === -1) {
        throw e;
      }
      return null;
    }
  }

  private async removeReplication(existingReplication: any) {
    const replicatorDb = this.dbInstance.use(REPLICATOR_DB);
    await replicatorDb.destroy(
      existingReplication._id,
      existingReplication._rev,
    );
  }
}
